// controllers
import AnimationController from '../../../assets/js/controllers/animation';
import ResizeController from '../../../assets/js/controllers/resize';

// static constants
import Platform from '../../../assets/js/constants/platform';
import ProgressEvent from '../../progress/progress-event';
import TimerEvent from '../timer-event';
import SkrolliteEvent from '../../../components/skrollite/skrollite-event';

// helpers
import { getElementIndex, multiAddEventListener, multiClassList } from '../../../assets/js/helpers';

export default class AccordionTimer {

	static selector = 'timer-accordion';

	// class variables

	el = {};

	coords = {
		container: {
			width: 0
		},
		scrollItem: {
			width: 0
		},
		scrollItemsWrapper: {
			width: 0
		}
	};

	config = {
		time: 0,
		timeTransition: 0.5
	};

	controllers = {
		animation: null,
		resize: null
	};

	flags = {
		isMobile: false,
		isMouseDown: false,
		isPaused: false,
		isTimerChange: false,
		isUserPaused: false
	};

	states = {
		index: -1,
		indexAdjusted: -1,
		lastTime: 0,
		timeElapsed: 0,
		timeRemaining: 0,
		timeRemainingTransition: 0,
		itemsTotal: 0,
		platform: null,
		time: 0
	};

	// constructor

	constructor(container) {
		this.el = {
			container,

			scrollWrapper: container.querySelector(`.${this.constructor.selector}__scroll-wrapper`),
			scrollItemsWrapper: container.querySelector(`.${this.constructor.selector}__scroll-wrapper > ul`),
			scrollBar: container.querySelector(`.${this.constructor.selector}__scroll-bar > figure:first-child`),
			scrollProgress: container.querySelector(`.${this.constructor.selector}__scroll-progress`),

			items: container.querySelectorAll(`.${this.constructor.selector}__item`),
			timers: container.querySelectorAll(`.${this.constructor.selector}__item-progress-bar`),
			timerProgresses: container.querySelectorAll('.progress-toggle-bar__bar-progress')
		};

		this.states.itemsTotal = this.el.items.length;
		this.config.time = parseFloat(this.el.container.getAttribute('data-time')) * 1000;
		this.states.time = this.config.time;

		this.initialize();
	}

	// init

	initialize() {
		this.#addEventListeners();
		this.changeItemByIndex(0, false);
	}

	// event listeners

	#addEventListeners = () => {
		multiAddEventListener(this.el.items, 'click', this.#onItemClick);
		multiAddEventListener(this.el.timers, 'click', this.#onTimerClick);

		this.el.scrollWrapper.addEventListener(ProgressEvent.ADJUST, this.#onProgressAdjust);

		this.el.container.addEventListener(TimerEvent.ADD_TIME, this.#onAddTime);
		this.el.container.addEventListener(TimerEvent.PAUSE, this.#onTimerPause);
		this.el.container.addEventListener(TimerEvent.PLAY, this.#onTimerPlay);
		this.el.container.addEventListener(TimerEvent.SEEK, this.#onTimerSeek);
		this.el.container.addEventListener(TimerEvent.POLL_STATUS, this.#onPollStatus);

		this.el.container.addEventListener(SkrolliteEvent.VIEWPORT_IN, this.#onViewportIn);
		this.el.container.addEventListener(SkrolliteEvent.VIEWPORT_OUT, this.#onViewportOut);

		this.el.container.addEventListener('mousedown', this.#onMouseDown);
		this.el.container.addEventListener('touchstart', this.#onTouchStart);

		document.body.addEventListener('mouseup', this.#onMouseUp);
		document.body.addEventListener('touchend', this.#onTouchEnd);

		this.controllers.animation = AnimationController.set({
			update: this.#onAnimationUpdate,
			render: this.#onAnimationRender
		});

		this.controllers.resize = ResizeController.set({
			update: this.#onResizeUpdate,
			render: this.#onResizeRender
		});
	}

	#removeEventListeners = () => {
		this.el.scrollWrapper.removeEventListener(ProgressEvent.ADJUST, this.#onProgressAdjust);

		this.el.container.removeEventListener(TimerEvent.ADD_TIME, this.#onAddTime);
		this.el.container.removeEventListener(TimerEvent.PAUSE, this.#onTimerPause);
		this.el.container.removeEventListener(TimerEvent.PLAY, this.#onTimerPlay);
		this.el.container.removeEventListener(TimerEvent.SEEK, this.#onTimerSeek);
		this.el.container.removeEventListener(TimerEvent.POLL_STATUS, this.#onPollStatus);

		this.el.container.removeEventListener(SkrolliteEvent.VIEWPORT_IN, this.#onViewportIn);
		this.el.container.removeEventListener(SkrolliteEvent.VIEWPORT_OUT, this.#onViewportOut);

		this.el.container.removeEventListener('mousedown', this.#onMouseDown);
		this.el.container.removeEventListener('touchstart', this.#onTouchStart);

		document.body.removeEventListener('mouseup', this.#onMouseUp);
		document.body.removeEventListener('touchend', this.#onTouchEnd);

		AnimationController.clear(this.controllers.animation);
		ResizeController.clear(this.controllers.resize);
	}

	// listener methods

	#onItemClick = (e) => {
		const $item = e.currentTarget;
		if (!$item.classList.contains('is-active')) {
			this.flags.isUserPaused = false;
			this.changeItem($item);
		}
	}

	#onTimerClick = (e) => {
		const $target = e.currentTarget;
		if ($target.classList.contains('is-playing')) {
			this.flags.isUserPaused = true;
			this.timerPause();
		} else {
			this.flags.isUserPaused = false;
			this.timerPlay();
		}
	}

	#onPollStatus = () => {
		const $activeItem = this.el.container.querySelector(`.${this.constructor.selector}__item.is-active`);
		this.el.container.dispatchEvent(new CustomEvent(TimerEvent.POLLING, { detail: { target: $activeItem, isPlaying: !this.flags.isPaused } }));
	}

	#onViewportIn = () => {
		if (!this.flags.isUserPaused) {
			this.timerPlay();
		}
	}

	#onViewportOut = () => {
		this.timerPause();
	}

	#onMouseDown = () => {
		this.flags.isMouseDown = true;
		this.states.isTimerChange = false;
	}

	#onMouseUp = () => {
		this.flags.isMouseDown = false;
	}

	#onTouchStart = () => {
		this.flags.isMouseDown = true;
		this.states.isTimerChange = false;
	}

	#onTouchEnd = () => {
		this.flags.isMouseDown = false;
	}

	#onProgressAdjust = (e) => {
		if (this.flags.isMobile) {
			const indexAdjusted = Math.round(e.detail.ratio * (this.states.itemsTotal - 1));
			if (!this.flags.isTimerChange || indexAdjusted !== this.states.index) {
				this.states.isTimerChange = false;
				this.states.indexAdjusted = indexAdjusted;
			}
		}
	}

	#onAddTime = (e) => {
		this.states.time = this.states.timeElapsed + parseInt(e.detail);
	}

	#onTimerPause = () => {
		this.timerPause();
	}

	#onTimerPlay = () => {
		this.flags.isUserPaused = false;
		this.timerPlay();
	}

	#onTimerSeek = (e) => {
		this.states.timeElapsed = e.detail.progress * 1000;
		this.states.timeRemaining = e.detail.duration * 1000 - this.states.timeElapsed;
		this.states.timeRemainingTransition = this.config.timeTransition;
	}

	// controller methods

	#onAnimationUpdate = () => {
		let willRender = false;

		this.updateTimer();
		willRender = !this.flags.isPaused;

		if (this.flags.isMobile) {
			willRender = (this.states.index !== this.states.indexAdjusted) || willRender;
		}

		return willRender;
	}

	#onAnimationRender = () => {
		if (!this.flags.isPaused) {
			this.renderTimer();
		}

		if (this.flags.isMobile) {
			this.checkScrollStatus();
		}
	}

	#onResizeUpdate = () => this.measureResize();
	#onResizeRender = () => this.adjustResize();

	//

	measureResize() {
		let willRender = ResizeController.isPlatformChange;

		this.states.platform = ResizeController.platform;
		this.coords.container.width = this.el.container.offsetWidth;
		this.coords.scrollItem.width = this.el.items[0].offsetWidth;
		this.coords.scrollItem.scrollWidth = this.el.items[0].scrollWidth;
		this.coords.scrollItemsWrapper.width = this.el.scrollItemsWrapper.offsetWidth;
		this.coords.scrollItemsWrapper.scrollWidth = this.el.scrollItemsWrapper.scrollWidth;

		if (this.states.platform === Platform.DESKTOP) {
			if (this.flags.isMobile) {
				this.flags.isMobile = false;
				willRender = true;
			}
		} else {
			if (!this.flags.isMobile) {
				this.flags.isMobile = true;
				willRender = true;
			}
		}

		return willRender;
	}

	adjustResize() {
		if (!this.flags.isMobile) {
			this.timerPause();
		}
	}

	// check index based on mobile scroll position

	checkScrollStatus() {
		if (this.states.index !== this.states.indexAdjusted) {
			this.changeItemByIndex(this.states.indexAdjusted);
		}
	}

	// change index

	changeItemByIndex(index, willPlay = true, autoLoop = true) {
		if (index !== this.states.index) {
			if (index < this.states.itemsTotal) {
				const $item = this.el.items[index];
				const $collapsible = $item.querySelector(`.${this.constructor.selector}__item-collapsible`);

				for (let i = 0; i < this.el.items.length; i++) {
					const $curItem = this.el.items[i];
					const $curCollapsible = $curItem.querySelector(`.${this.constructor.selector}__item-collapsible`);
					if ($curItem !== $item) {
						$curItem.classList.remove('is-active', 'is-active-in');
						$curCollapsible.style.height = '';
					}
				}

				$item.classList.add('is-active');
				$collapsible.style.height = `${$collapsible.children[0].offsetHeight}px`;

				this.states.index = index;
				this.states.indexAdjusted = index;

				this.resetTimer(willPlay);
				this.renderScrollProgress();

				this.el.container.dispatchEvent(new CustomEvent(TimerEvent.CHANGING, { detail: { target: $item, isPlaying: willPlay } }));

				AnimationController.one({
					update: () => true,
					render: () => {
						$item.classList.add('is-active', 'is-active-in');
					}
				});

				if (this.flags.isMobile && this.flags.isTimerChange) {
					this.el.scrollWrapper.scrollLeft = Math.round(this.states.index * $item.scrollWidth);
				}

			} else if (autoLoop) {
				this.changeItemByIndex(0);
			} else {
				this.timerPause();
			}
		}
	}

	changeItem($item) {
		this.changeItemByIndex(getElementIndex(this.el.items, $item));
	}

	//

	renderScrollProgress() {
		const ratio = this.states.index / this.states.itemsTotal;
		if (!this.flags.isMobile) {
			this.el.scrollProgress.dispatchEvent(new CustomEvent(ProgressEvent.ADJUST, { detail: { ratio } }));
		}
	}

	//

	startTimer() {
		this.timerPlay();
	}

	timerPause() {
		if (!this.flags.isPaused) {
			this.flags.isPaused = true;

			for (let i = this.el.timers.length - 1; i >= 0; i--) {
				const $timer = this.el.timers[i];
				$timer.classList.remove('is-playing');
			}

			const $activeItem = multiClassList(this.el.items, 'contains', 'is-active')[0];
			this.el.container.dispatchEvent(new CustomEvent(TimerEvent.PAUSING, { detail: { target: $activeItem, isPlaying: !this.flags.isPaused } }));
		}
	}

	timerPlay() {
		if (this.flags.isPaused) {
			this.flags.isPaused = false;
			this.states.lastTime = Date.now();

			for (let i = this.el.timers.length - 1; i >= 0; i--) {
				const $timer = this.el.timers[i];
				$timer.classList.add('is-playing');
			}

			const $activeItem = multiClassList(this.el.items, 'contains', 'is-active')[0];
			this.el.container.dispatchEvent(new CustomEvent(TimerEvent.PLAYING, { detail: { target: $activeItem, isPlaying: !this.flags.isPaused } }));
		}
	}

	resetTimer(willPlay = true) {
		this.states.time = this.config.time;
		this.states.timeElapsed = 0;
		this.states.timeRemaining = this.states.time;
		this.states.timeRemainingTransition = this.config.timeTransition;
		this.flags.isPaused = true;
		this.renderTimer();

		if (willPlay) {
			this.startTimer();
		}
	}

	updateTimer() {
		if (!this.flags.isPaused) {
			const timeCur = Date.now();
			this.states.timeElapsed += (timeCur - this.states.lastTime);
			this.states.timeRemaining = Math.max(0, this.states.time - this.states.timeElapsed);

			if (!this.states.timeRemaining) {
				this.states.timeRemainingTransition = Math.max(0, this.states.timeRemainingTransition - (timeCur - this.states.lastTime));
			}

			this.states.lastTime = timeCur;

			if (!this.states.timeRemainingTransition) {
				this.flags.isTimerChange = true;
				this.changeItemByIndex(this.states.index + 1);
			}
		}
	}

	renderTimer() {
		const ratioTimeRemaining = (this.states.time - this.states.timeRemaining) / this.states.time;
		this.el.timerProgresses[this.states.index].style.transform = `scaleX(${ratioTimeRemaining})`;
	}

	// destroy

	destroy() {
		this.#removeEventListeners();
	}

}
